import { useImmerAtom } from 'jotai-immer';
import { getDefaultStore } from 'jotai';
import { tableFiltersAtom, tableStateAtom, tableStatsAtom, tableViewAtom } from '../feeds_list_atoms';
import { ListTableStateHookReturn, TblPagination, TblSortDir, TblView } from '../../../../core/table/table.types';
import { useCallback } from 'react';
import { tblSpecSetFilter, tblSpecSetSort } from '../../../../core/table/spec';
import { feedsListAllColumnsView, feedsListColumnSpec } from './FeedsListTableSpec';
import { tblSpecLoadView, setCurrentTableView } from '../../../../core/table/tableViewsRepo';
import { FEEDS_LIST_VIEW_KEY } from '../../feeds_const';

// Type guard for pgsize property
function hasPgsize(view: any): view is { pgsize: number } {
    return typeof view.pgsize === 'number';
}

type FeedsListTableStateHookReturn = ListTableStateHookReturn & { setCurrentViewId: (viewId: string) => void };

export const useFeedsListTableState = (): FeedsListTableStateHookReturn => {
    const [state, setState] = useImmerAtom(tableStateAtom);
    const [view, setView] = useImmerAtom(tableViewAtom);
    const [filters, setFilters] = useImmerAtom(tableFiltersAtom);
    const [stats, setStats] = useImmerAtom(tableStatsAtom);

    const setPaging = useCallback(
        (paging: Partial<TblPagination>): void => {
            setStats(draft => {
                if (paging.page !== undefined) {
                    draft.paging.page = paging.page;
                }
                if (paging.size !== undefined) {
                    draft.paging.size = paging.size;
                }
                if (paging.total !== undefined) {
                    draft.paging.total = paging.total;
                }
            });
        },
        [setStats],
    );

    const setSort = useCallback(
        (col: string, dir?: TblSortDir): void => {
            const cols = getDefaultStore().get(tableViewAtom).cols;
            const newSort = tblSpecSetSort(col, cols, dir);
            console.log('[FeedsTableState] setSort called:', { col, dir, result: JSON.stringify(newSort) });
            setView(draft => {
                draft.sort = newSort;
            });
        },
        [setView],
    );

    const setFilter = useCallback(
        (key: string, val: any): void => {
            const cols = getDefaultStore().get(tableViewAtom).cols;
            setFilters(draft => tblSpecSetFilter(draft, key, cols, val));
        },
        [setFilters],
    );

    const setWait = useCallback(
        (waiting: boolean): void => {
            setState(draft => {
                draft.waiting = waiting;
            });
        },
        [setState],
    );

    const setCurrentViewId = useCallback(
        (viewId: string): void => {
            setState(draft => {
                draft.currentViewId = viewId;
            });
            // Read views on-demand to avoid capturing state in the dep array
            const views = getDefaultStore().get(tableStateAtom).views;
            // Robustly find the new view spec from views
            let newViewSpec = views.find(v => v.key === viewId || (v as any).id === viewId);
            // If switching to Default, always use canonical Default view spec and ignore persisted/legacy columns
            const isDefaultView =
                viewId === '1' ||
                viewId === 'default' ||
                viewId === 'all' ||
                (newViewSpec && (newViewSpec.key === '1' || newViewSpec.title === 'Default'));
            if (isDefaultView) {
                // newViewSpec = getFeedsInitialView();
                newViewSpec = feedsListAllColumnsView();
                console.warn('[setCurrentViewId] Forcing canonical Default view spec for Default view switch');
            } else if (newViewSpec && !('sections' in newViewSpec)) {
                // newViewSpec = getFeedsInitialView();
                newViewSpec = feedsListAllColumnsView();
                console.warn('[setCurrentViewId] Replacing legacy Default view with canonical Default view spec');
            } else if (!newViewSpec && isDefaultView) {
                // newViewSpec = getFeedsInitialView();
                newViewSpec = feedsListAllColumnsView();
                // Explicitly set sort from Default spec, with guard for undefined sortby
                setView((draft: TblView) => {
                    const sortby = typeof newViewSpec?.sortby === 'string' ? newViewSpec.sortby : '';
                    draft.sort = {
                        col: sortby.replace(/^-/, ''),
                        dir: sortby.startsWith('-') ? 'DSC' : 'ASC',
                    };
                });
            }
            if (newViewSpec) {
                const allCols = Array.isArray(newViewSpec.sections)
                    ? newViewSpec.sections.flatMap((section: any) => (Array.isArray(section.cols) ? section.cols : []))
                    : [];
                // Use feedsListColumnSpec for mapping
                const feedsListColMap = feedsListColumnSpec();
                // Only include columns present in the selected view spec
                const colMap = new Map();
                allCols.forEach(colKey => {
                    if (feedsListColMap.has(colKey)) {
                        colMap.set(colKey, feedsListColMap.get(colKey));
                    } else {
                        console.warn('[setCurrentViewId] colKey missing from feedsListColMap:', colKey);
                    }
                });
                const loadedView = tblSpecLoadView(viewId, [newViewSpec], colMap);
                setView((draft: TblView) => {
                    if (loadedView) {
                        Object.assign(draft, loadedView);
                    } else {
                        console.warn('[setCurrentViewId] loadedView is undefined for viewId:', viewId);
                    }
                });
                // Update paging size in stats if pgsize is present and valid (legacy or modern)
                if (hasPgsize(newViewSpec) && newViewSpec.pgsize > 0) {
                    setStats(draft => {
                        draft.paging.size = newViewSpec.pgsize;
                    });
                }
            } else {
                console.warn('[setCurrentViewId] newViewSpec not found for viewId:', viewId);
            }
            // Persist the current view selection to storage (session and user prefs)
            setCurrentTableView(FEEDS_LIST_VIEW_KEY, viewId).catch(e => {
                console.warn('Failed to persist current view selection:', e);
            });
        },
        [setState, setView, setStats],
    );

    return {
        waiting: state.waiting,
        views: state.views,
        currentViewId: state.currentViewId,
        cols: view.cols,
        sections: view.sections,
        sort: view.sort,
        filters: filters,
        fltStats: stats.fltStats,
        paging: stats.paging,
        setWait,
        setSort,
        setFilter,
        setPaging,
        setCurrentViewId,
    };
};
